"use client";

import { useEffect } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { SocketConnector } from "@/@sdk/@libs/socket-connector";
import type { iNotification } from "@/@sdk/notifications/notifications.models";
import { playNotificationSound } from "@/components/notifications/play-notification-sound";

export const NOTIFICATIONS_CHANGED_EVENT = "notifications:changed";

const POLL_INTERVAL_MS = 30_000;
const SOCKET_ATTACH_INTERVAL_MS = 2_000;

function notifyBadgeRefresh() {
  window.dispatchEvent(new CustomEvent(NOTIFICATIONS_CHANGED_EVENT));
}

function showNotificationToast(
  notification: iNotification,
  navigate: (to: string) => void
) {
  toast(notification.title, {
    description: notification.body,
    position: "bottom-right",
    action: notification.link
      ? {
          label: "View",
          onClick: () => {
            navigate(notification.link);
          },
        }
      : undefined,
  });
}

/**
 * Live WebSocket listener for NOTIFICATION_NEW: chime + toast + badge refresh.
 * Also polls every 30s so the unread badge stays resilient if a push is missed.
 */
export function useNotificationRealtime() {
  const navigate = useNavigate();

  useEffect(() => {
    const onNotificationNew = (notification: iNotification) => {
      playNotificationSound();
      showNotificationToast(notification, navigate);
      notifyBadgeRefresh();
    };

    let attached: typeof SocketConnector.socket | null = null;

    const attach = () => {
      const socket = SocketConnector.socket;
      if (!socket || socket === attached) return;
      attached?.off("NOTIFICATION_NEW", onNotificationNew);
      socket.on("NOTIFICATION_NEW", onNotificationNew);
      attached = socket;
    };

    attach();
    const attachInterval = window.setInterval(
      attach,
      SOCKET_ATTACH_INTERVAL_MS
    );
    const pollInterval = window.setInterval(
      notifyBadgeRefresh,
      POLL_INTERVAL_MS
    );

    return () => {
      window.clearInterval(attachInterval);
      window.clearInterval(pollInterval);
      attached?.off("NOTIFICATION_NEW", onNotificationNew);
    };
  }, [navigate]);
}
